/** PDF de extrato por conta dentro de um dia. */
export type FinanceiroExtratoContaFile = {
  /** Número da conta (nome do arquivo sem .pdf). */
  account: string
  fileName: string
  /** Referência ao File local (conteúdo ainda não lido). */
  file: File
}

/** Pasta de dia (01–31) com PDFs de conta. */
export type FinanceiroExtratoContaDay = {
  /** Dia normalizado com 2 dígitos (ex.: "02", "23"). */
  day: string
  /** Nome original da pasta. */
  folderName: string
  files: FinanceiroExtratoContaFile[]
}

/** Pasta de mês (01–12) com dias. */
export type FinanceiroExtratoContaMonth = {
  /** Mês normalizado com 2 dígitos (ex.: "01"). */
  month: string
  /** Nome original da pasta (ex.: "01 - JANEIRO"). */
  folderName: string
  /** Rótulo amigável (ex.: "Janeiro"). */
  label: string
  days: FinanceiroExtratoContaDay[]
  /** Total de PDFs válidos no mês. */
  pdfCount: number
}

/** Árvore carregada a partir da pasta do ano. */
export type FinanceiroExtratoContaTree = {
  rootName: string
  year: string
  months: FinanceiroExtratoContaMonth[]
  /** Pastas ignoradas (ex.: "CONFERÊNCIA DE SALDO"). */
  skippedFolderCount: number
  /** PDFs ignorados (nome não numérico). */
  skippedPdfCount: number
}

/** Linha plana para tabela de conferência / escopo selecionado. */
export type FinanceiroExtratoContaScopeRow = {
  month: string
  monthLabel: string
  day: string
  account: string
  fileName: string
  file: File
}

export type FinanceiroPelFolderStatus = 'processing' | 'complete'
export type FinanceiroPelFileStatus = 'processing' | 'ok' | 'error'

/** Pasta dia (YYYY/MM/DD) no controle PeL. */
export type FinanceiroPelStatementFolder = {
  id: string
  folder_key: string
  year: string
  month: string
  day: string
  root_folder_name: string | null
  files_ok: number
  files_error: number
  status: FinanceiroPelFolderStatus
  processed_by: string | null
  processed_at: string
  created_at: string
  updated_at: string
}

/** Arquivo PDF já enviado ao webhook PeL. */
export type FinanceiroPelStatementFile = {
  id: string
  folder_id: string
  year: string
  month: string
  day: string
  account: string
  file_name: string
  status: FinanceiroPelFileStatus
  error_message: string | null
  processed_by: string | null
  processed_at: string
  created_at: string
  updated_at: string
}

export type FinanceiroPelFileProgressStatus =
  | 'pendente'
  | 'enviando'
  | 'ok'
  | 'erro'
  | 'ja_processado'

export type FinanceiroPelFileProgress = {
  id: string
  year: string
  month: string
  day: string
  account: string
  fileName: string
  status: FinanceiroPelFileProgressStatus
  error?: string
}

export type BeginPelFolderInput = {
  year: string
  month: string
  day: string
  rootFolderName: string | null
  userId: string | null
}

export type RegisterPelFileInput = {
  folderId: string
  year: string
  month: string
  day: string
  account: string
  fileName: string
  status: FinanceiroPelFileStatus
  errorMessage?: string | null
  userId: string | null
}

export type UpdatePelFileStatusInput = {
  fileId: string
  status: 'ok' | 'error'
  errorMessage?: string | null
  userId: string | null
}

export type CompletePelFolderInput = {
  folderId: string
  arquivosOk: number
  arquivosErro: number
}

export class FinanceiroPelArquivoJaProcessadoError extends Error {
  readonly fileKey: string

  constructor(fileKey: string) {
    super(`O arquivo "${fileKey}" já foi processado.`)
    this.name = 'FinanceiroPelArquivoJaProcessadoError'
    this.fileKey = fileKey
  }
}

export type FinanceiroPelStatementType = 'debit' | 'credit'

/** Linha do extrato gravada em finan_pel_statements (n8n). */
export type FinanceiroPelStatement = {
  id: string
  folder_id: string | null
  file_id: string | null
  year: string
  month: string
  day: string
  account: string
  file_name: string
  amount: number | null
  type: FinanceiroPelStatementType | null
  description: string
  observation: string
  identification: string | null
  transaction_at: string | null
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

export type FinanceiroPelStatementsConsultaFilters = {
  year?: string | null
  month?: string | null
  /** Dia da pasta (valor gravado, ex.: "27" ou "07"). */
  day?: string | null
  account?: string | null
  type?: FinanceiroPelStatementType | null
  /** Data/hora da transação, início (YYYY-MM-DD). */
  dateFrom?: string | null
  /** Data/hora da transação, fim (YYYY-MM-DD). */
  dateTo?: string | null
  /** Data da pasta, início (YYYY-MM-DD). */
  folderDateFrom?: string | null
  /** Data da pasta, fim (YYYY-MM-DD). */
  folderDateTo?: string | null
  search?: string | null
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page: number
  pageSize: number
}

export type FinanceiroPelStatementsConsultaResult = {
  rows: FinanceiroPelStatement[]
  total: number
}
